import { Currency } from '@configs/language';
import { Currencies, DecimalDigits, WalletTypes } from '@constants/enums';
import { CURRENCY_OPTIONS } from '@efex-components/common/Form/config';
import { IWallet } from '@efex-services/client/types';
import { Tooltip } from '@efexteam/components';
import { useRole } from '@efexteam/permission-manager';
import AddIcon from '@mui/icons-material/Add';
import InfoIcon from '@mui/icons-material/Info';
import { Box, IconButton, Typography } from '@mui/material';
import { selectBusiness } from '@pages/profile/store/selectors';
import { DECIMALS_TO_SHOW, formatCurrency } from '@utils/formater';
import clsx from 'clsx';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import useWalletCard from './useWalletCard';
import WalletCardStyles from './WalletCard.styles';

interface WalletCardProps {
  wallet: IWallet;
  addFunds: (currency: Currencies, walletType: WalletTypes) => void;
  isGlobalAccount?: boolean;
}

const WalletCard = ({ wallet, addFunds, isGlobalAccount }: WalletCardProps) => {
  const { t } = useTranslation();
  const { isAwActive, getExchangeV2 } = useWalletCard();
  const { businessName } = useSelector(selectBusiness) || {
    businessName: 'EFEX',
  };
  const { isPromoter } = useRole();

  const originCurrency = useMemo(() => {
    if (isPromoter) {
      return Currencies.MXN;
    }
    return Currencies.USD;
  }, [isPromoter]);

  return (
    <Box className={`balance-step ${WalletCardStyles.card}`}>
      <Box className={WalletCardStyles.cardContent}>
        <Box>
          <Box className={WalletCardStyles.balanceTitle}>
            <Typography variant="body2">
              {wallet.currency.name} balance
            </Typography>
            {!isGlobalAccount && (
              <Tooltip
                title={t('afnWalletTooltip', {
                  businessName,
                })}
              >
                <InfoIcon fontSize="inherit" />
              </Tooltip>
            )}
          </Box>

          <Typography
            variant="h6"
            className={clsx({
              [WalletCardStyles.balanceAmount]: !isGlobalAccount,
            })}
          >
            {formatCurrency(
              wallet.balance,
              DecimalDigits.DEFAULT_DIGITS,
              DecimalDigits.DEFAULT_MIN_DIGITS,
              wallet.currency.name
            )}
            <span> {wallet.currency.name}</span>
          </Typography>
        </Box>

        <Box>
          {
            CURRENCY_OPTIONS.find(
              (currency) => currency.id === wallet.currency.name
            )?.icon
          }
          {isAwActive && wallet.currency.name !== Currencies.COP ? (
            <Tooltip title={t('afnTitle')} placement="top">
              <IconButton
                onClick={() =>
                  addFunds(
                    wallet.currency.name as Currencies,
                    wallet.wallet.wallet_type as WalletTypes
                  )
                }
              >
                <AddIcon />
              </IconButton>
            </Tooltip>
          ) : null}
        </Box>
      </Box>
      {wallet.blocked && parseFloat(wallet.blocked) > 0 ? (
        <Box className={WalletCardStyles.blockedBalance}>
          <Typography variant="caption">
            {t('walletLockedBalance')} {formatCurrency(wallet.blocked)}{' '}
            {wallet.currency.name}
          </Typography>
        </Box>
      ) : null}
      {wallet.currency.name !== originCurrency ? (
        <Box className={WalletCardStyles.exchangeContainer}>
          <Box>
            <Typography variant="caption">
              {t('walletExRateBuy', { currency: originCurrency })}
            </Typography>

            <Typography variant="caption">
              {formatCurrency(
                getExchangeV2(wallet.currency.name as Currency, originCurrency)
                  ?.buy ?? '',
                DECIMALS_TO_SHOW
              )}
            </Typography>
          </Box>

          <Box>
            <Typography variant="caption">
              {t('walletExRateSell', { currency: originCurrency })}
            </Typography>

            <Typography variant="caption">
              {formatCurrency(
                getExchangeV2(wallet.currency.name as Currency, originCurrency)
                  ?.sell ?? '',
                DECIMALS_TO_SHOW
              )}
            </Typography>
          </Box>
        </Box>
      ) : null}
    </Box>
  );
};

export default WalletCard;
